import React from 'react';
import { useTranslation } from 'react-i18next';
import useLogout from '@/features/auth/hooks/useLogout';
import { getAuthenticatedUsername } from '@/features/auth/utils/jwtPermissions';
import { storage } from '@/utils/storage';
import '../styles/AccountPage.css';

const AccountPage: React.FC = () => {
  const { t } = useTranslation();
  const { isLoggingOut, signOut } = useLogout();
  const username = getAuthenticatedUsername(storage.getAccessToken()) ?? t('account.summary.customer');
  const initial = username.charAt(0).toLocaleUpperCase();

  return (
    <div className="account-page">
      <header className="account-page-heading">
        <div>
          <p className="account-page-eyebrow">{t('account.eyebrow')}</p>
          <h1>{t('account.title')}</h1>
          <p>{t('account.subtitle')}</p>
        </div>
        <span className="account-authenticated-badge">
          <span aria-hidden="true">✓</span>
          {t('account.authenticated')}
        </span>
      </header>

      <div className="account-layout">
        <aside className="account-summary-card" aria-label={t('account.summary.label')}>
          <div className="account-avatar" aria-hidden="true">{initial}</div>
          <div className="account-summary-copy">
            <span>{t('account.summary.signed_in_as')}</span>
            <strong>{username}</strong>
          </div>

          <nav className="account-navigation" aria-label={t('account.navigation.label')}>
            <a href="#personal-information">
              <span className="material-symbols-outlined account-navigation-icon" aria-hidden="true">person</span>
              {t('account.navigation.profile')}
            </a>
            <a href="#shipping-address">
              <span className="material-symbols-outlined account-navigation-icon" aria-hidden="true">location_on</span>
              {t('account.navigation.address')}
            </a>
            <a href="#change-password">
              <span className="material-symbols-outlined account-navigation-icon" aria-hidden="true">lock</span>
              {t('account.navigation.password')}
            </a>
          </nav>

          <button
            type="button"
            className="account-logout-button"
            disabled={isLoggingOut}
            onClick={() => void signOut()}
          >
            <span className="material-symbols-outlined" aria-hidden="true">logout</span>
            {isLoggingOut ? t('account.actions.logging_out') : t('account.actions.logout')}
          </button>
        </aside>

        <div className="account-content">
          <section className="account-api-notice" role="status">
            <span className="account-api-notice-icon" aria-hidden="true">i</span>
            <div>
              <strong>{t('account.availability.title')}</strong>
              <p>{t('account.availability.description')}</p>
            </div>
          </section>

          <section id="personal-information" className="account-card">
            <div className="account-card-heading">
              <span className="material-symbols-outlined account-card-icon" aria-hidden="true">person</span>
              <div>
                <h2>{t('account.profile.title')}</h2>
                <p>{t('account.profile.description')}</p>
              </div>
            </div>

            <div className="account-field-grid">
              <div className="account-readonly-field">
                <span>{t('account.profile.username')}</span>
                <strong>{username}</strong>
              </div>
              <div className="account-readonly-field account-readonly-field-muted">
                <span>{t('account.profile.other_information')}</span>
                <strong>{t('account.unavailable')}</strong>
              </div>
            </div>
            <p className="account-section-note">{t('account.profile.api_note')}</p>
          </section>

          <section id="shipping-address" className="account-card">
            <div className="account-card-heading">
              <span className="material-symbols-outlined account-card-icon" aria-hidden="true">location_on</span>
              <div>
                <h2>{t('account.address.title')}</h2>
                <p>{t('account.address.description')}</p>
              </div>
            </div>
            <div className="account-empty-section">
              <strong>{t('account.address.unavailable_title')}</strong>
              <p>{t('account.address.unavailable_description')}</p>
            </div>
          </section>

          <section id="change-password" className="account-card">
            <div className="account-card-heading">
              <span className="material-symbols-outlined account-card-icon" aria-hidden="true">lock</span>
              <div>
                <h2>{t('account.password.title')}</h2>
                <p>{t('account.password.description')}</p>
              </div>
            </div>
            <div className="account-empty-section">
              <strong>{t('account.password.unavailable_title')}</strong>
              <p>{t('account.password.unavailable_description')}</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
